/**
 * Labstep
 *
 * @module components/Metadata/Action/Create/Modal
 * @desc Modal to create a metadata
 */

import { CanCreate } from 'labstep-web/components/Entity/Can';
import { ActionComponentModalContainer as ActionComponent } from 'labstep-web/core/Action/Component';
import { useResetForm } from 'labstep-web/core/Form/Reset/utils';
import { ModalDefaultContainer as ModalDefault } from 'labstep-web/core/Modal/Default';
import { Experiment } from 'labstep-web/models/experiment.model';
import { ProtocolValue } from 'labstep-web/models/protocol-value.model';
import { enhanceOptions } from 'labstep-web/services/utils.service';
import type React from 'react';
import { ProtocolValueFormCreate } from '..';
import type {
  ProtocolValueActionCreateContainerProps,
  ProtocolValueActionCreateProps,
} from './types';

export const ProtocolValueFormCreateModal: React.FC<
  ProtocolValueActionCreateProps
> = ({
  experiment,
  actionComponentProps,
  options,
  modalProps,
  isHeaderPlural = false,
  entityName,
  disableAddAnother,
  dataCy,
  isInput,
  initialName,
  ...rest
}) => {
  const { key, submitButton, onSuccess } =
    useResetForm(disableAddAnother);

  return (
    <ModalDefault
      size="tiny"
      header={`Add ${isInput ? 'Input' : 'Output'} ${ProtocolValue.getHumanReadableEntityName(false, true)}`}
      viewComponent={({ toggleModal }): React.ReactElement => (
        <ActionComponent
          dataCy={dataCy}
          dataTestId="protocol-value-action-create"
          onClick={toggleModal}
          type="button"
          text={`Add ${ProtocolValue.getHumanReadableEntityName(false, true)}`}
          {...actionComponentProps}
        />
      )}
      content={({ toggleModal }): React.ReactElement => (
        <div>
          <ProtocolValueFormCreate
            key={key}
            experiment={experiment}
            defaultValues={{
              ...(rest as any).defaultValues,
              ...(initialName && initialName.length > 0
                ? { name: initialName }
                : {}),
            }}
            options={enhanceOptions({
              options,
              toast: true,
              onSuccess: () => onSuccess(toggleModal),
            })}
            submitButton={submitButton}
            isInput={isInput}
            {...rest}
          />
        </div>
      )}
      {...modalProps}
    />
  );
};

export const ProtocolValueFormCreateModalContainer: React.FC<
  ProtocolValueActionCreateContainerProps
> = ({ experiment, ...rest }) => (
  <CanCreate
    parentName={Experiment.entityName}
    parentId={experiment.idAttr}
    entityName={ProtocolValue.entityName}
  >
    <ProtocolValueFormCreateModal {...rest} experiment={experiment} />
  </CanCreate>
);
